import React, { useCallback, useContext, useState } from 'react'
import { Stack, StackItem, Text, TextField } from '@fluentui/react'
import { AppStateContext } from '../../state/AppProvider'

import { Conversation } from '../../api/models'

import { ChatHistoryListItemGroups } from './ChatHistoryListItem'
import debounce from 'lodash-es/debounce'

interface ChatHistoryListProps { }

export interface GroupedChatHistory {
  month: string
  entries: Conversation[]
}

const groupByMonth = (entries: Conversation[]) => {
  const groups: GroupedChatHistory[] = [{ month: 'Recent', entries: [] }]
  const currentDate = new Date()

  entries.forEach(entry => {
    const date = new Date(entry.date)
    const daysDifference = (currentDate.getTime() - date.getTime()) / (1000 * 60 * 60 * 24)
    const monthYear = date.toLocaleString('default', { month: 'long', year: 'numeric' })
    const existingGroup = groups.find(group => group.month === monthYear)

    if (daysDifference <= 7) {
      groups[0].entries.push(entry)
    } else {
      if (existingGroup) {
        existingGroup.entries.push(entry)
      } else {
        groups.push({ month: monthYear, entries: [entry] })
      }
    }
  })

  groups.sort((a, b) => {
    // Check if either group has no entries and handle it
    if (a.entries.length === 0 && b.entries.length === 0) {
      return 0 // No change in order
    } else if (a.entries.length === 0) {
      return 1 // Move 'a' to a higher index (bottom)
    } else if (b.entries.length === 0) {
      return -1 // Move 'b' to a higher index (bottom)
    }
    const dateA = new Date(a.entries[0].date)
    const dateB = new Date(b.entries[0].date)
    return dateB.getTime() - dateA.getTime()
  })

  groups.forEach(group => {
    group.entries.sort((a, b) => {
      const dateA = new Date(a.date)
      const dateB = new Date(b.date)
      return dateB.getTime() - dateA.getTime()
    })
  })

  return groups
}

const ChatHistoryList: React.FC<ChatHistoryListProps> = () => {
  const appStateContext = useContext(AppStateContext)
  const chatHistory = appStateContext?.state.chatHistory || []
  const [filteredChatHistory, setFilteredChatHistory] = useState([...chatHistory])

  React.useEffect(() => {
    setFilteredChatHistory([...chatHistory])
  }, [appStateContext?.state.chatHistory])

  const handleHistoryFilter = useCallback(
    debounce((newValue: string | undefined = '') => {
      const filteredValues = chatHistory.filter(chat => chat.title.toLowerCase().indexOf(newValue.toLowerCase()) > -1)
      setFilteredChatHistory(filteredValues)
    }, 500),
    []
  )

  return (
    <>
      <TextField
        iconProps={{ iconName: 'CustomSearchIcon' }}
        placeholder='Search'
        styles={{
          root: { marginTop: '32px', marginBottom: '46px', paddingRight: '16px' },
          prefix: { background: 'transparent', padding: '0, 4px, 0 12px' },
          fieldGroup: { borderRadius: '8px', height: '40px', borderColor: "#bfbfbf" },
          field: {
            '::placeholder': {
              color: '#B0B0B0',
              fontFamily: 'Arial',
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: '24px'
            },
          },
        }}
        onChange={(_e, newValue) => handleHistoryFilter(newValue)}
      />
      {filteredChatHistory?.length > 0 ? (
        <ChatHistoryListItemGroups groupedChatHistory={groupByMonth(filteredChatHistory)} />
      ) : (
        <Stack horizontal horizontalAlign="center" verticalAlign="center" style={{ width: '100%', marginTop: 10 }}>
          <StackItem>
            <Text style={{ alignSelf: 'center', fontWeight: '400', fontSize: 14 }}>
              <span>No chat history.</span>
            </Text>
          </StackItem>
        </Stack>
      )}
    </>
  )
}

export default ChatHistoryList
